// #region imports
import { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Title } from '../../components/Title';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Container,
    Divider,
    CardHeader,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Snackbar,
    Alert,
    TextField,
    Select,
    Checkbox,
    IconButton,
} from '@mui/material';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import { useForm, Controller } from 'react-hook-form';
import type { Tax } from '../../types/reseller';
import { resellerService } from '../../reseller.service';
import {
    CartProduct,
    CartProductResponse,
    CartDiscount,
    cartService,
    CartCheckoutRequest,
    CheckoutCreateRequest,
    IndustrialCheckoutData,
    IndustrialProduct,
} from '../../cart.service';
import { useMounted } from '../../hooks/use-mounted';
import { Scrollbar } from '../../components/Scrollbar';
import numeral from 'numeral';
import CreditCard from 'react-credit-cards';
import { formatCreditCardNumber, formatExpirationDate, formatCVC } from '../../utils/credit-cards';
import 'react-credit-cards/es/styles-compiled.css';
import creditCards from '../../images/credit-cards.png';
import { IToastState, defaultToastState } from '../../components/ToastState';
import type { Country } from '../../types/helper';
import { usaStatesAndTerritories } from '../../utils/cart';
import { canadaProvinces } from '../../utils/cart';
import { settings } from '../../settings';
import styled from '@emotion/styled';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { paypalClientId } from '../../AppSettings';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import {
    LIFETIME_SUPPORT_YEARS,
    ANNUAL_SUPPORT_GENERAL,
    ANNUAL_SUPPORT_ID,
    ANNUAL_SUPPORT_NAME,
    SITE_LICENSE,
    ENTERPRISE_LICENSE,
    UNLIMITED_LICENSE,
    ANNUAL_SUPPORT_SITE,
    ANNUAL_SUPPORT_ENTERPRISE,
    ANNUAL_SUPPORT_UNLIMITED,
} from '../../AppSettings';
// #endregion

// #region styles
const PromoCard = styled(Card)(() => ({
    height: '100%',
}));

const PromoLink = styled.a`
    color: #007bff;
    text-decoration: underline;
    cursor: pointer;
`;

const PromoDialogLink = styled.a`
    text-decoration: none;
    cursor: pointer;
`;

const PromoTitle = styled.div`
    font-size: 24px;
    text-align: center;
`;
const PromoSubtitle = styled.div`
    font-size: 16px;
    font-style: italic;
    text-align: center;
    margin-bottom: 20px;
`;

const PromoProduct = styled.div`
    padding: 15px;
    height: 100%;
    text-align: center;
    img {
        display: block;
        margin: 0 auto;
    }
`;

const PromoName = styled.h3`
    font-family: 'Segoe UI', 'Roboto', 'Geneva', 'Verdana', 'sans-serif';
    font-weight: 700;
    font-size: 14px;
    margin-bottom: 8px;
    margin-top: 10px;
    color: #0d2366;
    line-height: 20px;
    letter-spacing: 0.6px;
`;
const PromoDescr = styled.p`
    font-family: 'Segoe UI', Roboto, Geneva, Verdana, sans-serif;
    font-size: 12px;
    font-weight: 400;
    text-align: left;
    line-height: 20px;
    color: #5b6070;
    margin-top: 10px;
`;
const PromoMore = styled.p`
    text-align: center;
    color: #007bff;
    a,
    a:visited {
        text-decoration: none;
        color: rgb(24, 117, 209);
    }
`;

const VertDivider = styled.div`
    border-left: 1px solid #e0e0e0;
    height: 100px;
    margin-left: 20px;
    margin-right: 20px;
`;

// #endregion

const translateCardType = (issuer: string): string => {
    switch (issuer) {
        case 'visa':
            return 'VISA';
        case 'mastercard':
            return 'MC';
        case 'amex':
            return 'AMEX';
        case 'dinersclub':
            return 'DINE';
        case 'discover':
            return 'DISC';
        default:
            return '';
    }
};

interface FormData {
    email: string;
    firstName: string;
    lastName: string;
    company: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    phone: string;
}
function emptyFormData(): FormData {
    return {
        email: '',
        firstName: '',
        lastName: '',
        company: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        phone: '',
    };
}
function removePrefix(str, prefix) {
    if (str.startsWith(prefix)) {
        return str.slice(prefix.length);
    }
    return str;
}
interface DiscountFormData {
    discountCode: string;
}

export const CartCheckout = () => {
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogTitle, setDialogTitle] = useState('');
    const [openInvoiceDialog, setOpenInvoiceDialog] = useState(false);
    const [openBlacklistedDialog, setOpenBlacklistedDialog] = useState(false);
    const [openMonerisDialog, setOpenMonerisDialog] = useState(false);
    const [openPromoDialog, setOpenPromoDialog] = useState(false);
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [invoiceDisabled, setInvoiceDisabled] = useState(false);
    const [failMessage, setFailMessage] = useState('');
    // Set when the API blocks a create-order as a recent duplicate (409). Drives
    // the "View your order" link in the failure dialog (-> /invoices/{id}).
    const [duplicateOrderId, setDuplicateOrderId] = useState('');
    const [toastState, setToastState] = useState<IToastState>(defaultToastState);
    const [inProgress, setInProgress] = useState<boolean>(false);

    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const isMounted = useMounted();

    // product info obtained from the server
    const [productInfo, setProductInfo] = useState<CartProductResponse | null>(null);
    // cart products
    const [products, setProducts] = useState<CartProduct[]>([]);
    // industrial cart products
    const [industrialProducts, setIndustrialProducts] = useState<IndustrialProduct[]>([]);
    // raw industrial cart config (for server-side price recomputation at checkout)
    const [industrialRaw, setIndustrialRaw] = useState<IndustrialCheckoutData | null>(null);

    const [upgraded, setUpgraded] = useState<boolean>(false);
    const [total, setTotal] = useState<number>(0);
    const [subtotal, setSubtotal] = useState<number>(0);
    const [discount, setDiscount] = useState<number>(0); // current discount (main or promo product)
    const [mainDiscount, setMainDiscount] = useState<number>(0); // main product discount saved (if discount code applied)
    const [discountCode, setDiscountCode] = useState<string>('');
    const [reseller, setReseller] = useState<number>(0);
    const [taxes, setTaxes] = useState<Tax[]>([]);
    const [gst, setGst] = useState<number>(0);
    const [pst, setPst] = useState<number>(0);

    const [paypalCheckoutToken, setPaypalCheckoutToken] = useState<string>('');
    const paypalOrderRef = useRef<{ token: string; orderId: string } | null>(null);
    // Carries the server's real create-order failure reason from
    // handlePayPalCreateOrder to the SDK onError callback (which fires after the
    // createOrder promise rejects). Null for SDK-internal errors → generic text.
    const paypalErrorRef = useRef<string | null>(null);
    // Carries the existing OrderID from a duplicate-order 409 raised in
    // handlePayPalCreateOrder out to the PayPal onError handler (which runs
    // after the SDK rejects), so it can offer the "View your order" link too.
    const paypalDuplicateOrderRef = useRef<string | null>(null);
    const [billing] = useState<FormData | null>(emptyFormData());
    const [countries, setCountries] = useState<Country[]>([]);
    const [country, setCountry] = useState<string>('');
    const [canadaProvince, setCanadaProvince] = useState<number>(0);
    const [usaState, setUsaState] = useState<number>(0);

    const [gotoHome, setGotoHome] = useState<boolean>(false);

    const { handleSubmit, control, watch, trigger } = useForm<FormData>();
    const paypalWatch = {
        firstName: watch('firstName'),
        lastName: watch('lastName'),
        address: watch('address'),
        city: watch('city'),
        zip: watch('zip'),
        state: watch('state'),
        email: watch('email'),
        phone: watch('phone'),
    };

    const {
        register: registerForm1,
        handleSubmit: handleSubmitForm1,
        formState: { errors: errorsForm1 },
    } = useForm<DiscountFormData>();

    const onPromoInfoClick = () => {
        setOpenPromoDialog(true);
    };

    const showError = (message: string) => {
        setFailMessage(message);
        setOpenDialog(true);
        setInProgress(false);
        setGotoHome(true);
    };

    const onSubmitDiscountForm = async (data) => {
        if (data.discountCode) {
            // apply discount code not only to main product but also to promo product whatever is currently in the cart
            const fetchedDiscount: CartDiscount = await cartService.getDiscount(
                subtotal,
                data.discountCode,
                products[0].productId,
                products[0].licenseId,
                products[0].qty
            );
            if (fetchedDiscount.discount === 0) {
                setToastState({ open: true, status: 'error', message: 'Failed to apply discount code' });
            } else {
                setToastState({ open: true, status: 'success', message: 'Discount code applied' });
            }
            setDiscount(fetchedDiscount.discount);
            setDiscountCode(data.discountCode);
            setMainDiscount(fetchedDiscount.discount); // save main product discount for later
            setReseller(fetchedDiscount.resellerId);
            const tax = calcTax(subtotal - fetchedDiscount.discount, country, canadaProvinces[canadaProvince].short);
            calcTotal(products, fetchedDiscount.discount, tax);
        }
    };
    const [cardNumber, setCardNumber] = useState<string>('');
    const [expiry, setExpiry] = useState<string>('');
    const [cvc, setCvc] = useState<string>('');
    const [focused, setFocused] = useState<string>('');
    const [issuer, setIssuer] = useState<string>('');

    const inProgressRef = useRef(false);

    const loadData = useCallback(async () => {
        try {
            const sid = searchParams.get('sid');
            if (sid !== null && sid !== '') {
                const response = await cartService.getIndustrialProducts(sid);
                if (!response.ok) {
                    if (response.message && response.message !== '') {
                        showError(response.message);
                    } else {
                        showError('Failed to load cart data');
                    }
                } else {
                    const fetchedProducts = response.body;
                    if (isMounted()) {
                        calcIndustrialTotal(fetchedProducts, gst + pst);
                        setIndustrialProducts(fetchedProducts);
                    }
                    const rawResponse = await cartService.getIndustrialRawCart(sid);
                    if (rawResponse.ok && isMounted()) {
                        setIndustrialRaw(rawResponse.body);
                    }
                }
            } else {
                // regular product
                const pid = parseInt(searchParams.get('pid'), 10);
                const lic = parseInt(searchParams.get('lic'), 10);
                const qty = parseInt(searchParams.get('qty'), 10);
                let paramsOk = true;
                if (isNaN(pid) || isNaN(lic) || isNaN(qty) || (lic < 0 && qty > 1)) {
                    showError('Invalid product parameters');
                    paramsOk = false;
                }
                if (paramsOk) {
                    const response = await cartService.getProduct(pid, lic, qty);
                    if (!response.ok) {
                        showError(response.message);
                        return;
                    }
                    const fetchedProductInfo = response.body;
                    setProductInfo(fetchedProductInfo);
                    if (isMounted()) {
                        const DEFAULT_SUPPORT_QTY = 0;
                        const newProducts = [
                            fetchedProductInfo,
                            calculatedSupport(fetchedProductInfo, DEFAULT_SUPPORT_QTY, false),
                        ];
                        calcTotal(newProducts, discount, gst + pst);
                        setProducts(newProducts);
                    }
                }
            }
            // get countries list (for both industrial and regular products)
            if (isMounted()) {
                const fetchedCountries: Country[] = await resellerService.getCountries();
                setCountries(fetchedCountries);
                setCountry('US');
            }
        } catch (err) {
            console.error(err);
        }
    }, [isMounted]);

    useEffect(() => {
        loadData();
    }, []);

    // supportQty - 0: 1 year, 1: 2 years, 2: 3 years, 4: 5 years, 9: 10 years, LIFETIME_SUPPORT_YEARS: lifetime
    // supportQty - how many EXTRA years of support to add
    // price depends on the license type and qty of product (as well as promo product)
    const calculatedSupport = (
        productInfo: CartProductResponse,
        supportQty: number,
        upgraded: boolean
    ): CartProduct => {
        let supportPrice = 0;
        switch (productInfo.licenseId) {
            case SITE_LICENSE:
                supportPrice = ANNUAL_SUPPORT_SITE;
                break;
            case ENTERPRISE_LICENSE:
                supportPrice = ANNUAL_SUPPORT_ENTERPRISE;
                break;
            case UNLIMITED_LICENSE:
                supportPrice = ANNUAL_SUPPORT_UNLIMITED;
                break;
            default:
                supportPrice = ANNUAL_SUPPORT_GENERAL;
                break;
        }
        if (upgraded) {
            supportPrice = supportPrice * productInfo.promoQty;
        } else {
            supportPrice = supportPrice * productInfo.qty;
        }
        const supportProduct: CartProduct = {
            productId: ANNUAL_SUPPORT_ID,
            productName: ANNUAL_SUPPORT_NAME,
            licenseId: productInfo.licenseId,
            licenseName: productInfo.licenseName,
            price: supportPrice,
            qty: supportQty,
            amount: supportPrice * supportQty,
        };
        return supportProduct;
    };

    const calcTotal = (products: CartProduct[], discount: number, tax: number): void => {
        let total = 0;
        let subtotal = 0;
        products.forEach((product) => {
            subtotal += product.amount;
        });
        const beforeTaxes = subtotal - discount;
        total = beforeTaxes + tax;
        setTotal(total);
        setSubtotal(subtotal);
    };

    const calcIndustrialTotal = (products: IndustrialProduct[], tax: number): void => {
        let total = 0;
        let subtotal = 0;
        products.forEach((product) => {
            subtotal += product.amt;
        });
        total = subtotal + tax;
        setTotal(total);
        setSubtotal(subtotal);
    };

    // #region handlers
    const handleSupportChange = (event) => {
        const newProducts = [...products];
        const supportIdx = newProducts.findIndex((item) => item.productId === ANNUAL_SUPPORT_ID);
        newProducts[supportIdx].qty = event.target.value;
        newProducts[supportIdx].amount = newProducts[supportIdx].price * newProducts[supportIdx].qty;
        setProducts(newProducts);
        const newSubtotal = newProducts.reduce((acc, item) => acc + item.amount, 0);
        const tax = calcTax(newSubtotal - discount, country, canadaProvinces[canadaProvince].short);
        calcTotal(newProducts, discount, tax);
    };

    const handleCallback = ({ issuer }, isValid) => {
        if (isValid) {
            setIssuer(issuer);
        } else {
            setIssuer('');
        }
    };

    const handleInputFocus = ({ target }) => {
        setFocused(target.name);
    };

    const handleInputChange = ({ target }) => {
        if (target.name === 'number') {
            target.value = formatCreditCardNumber(target.value);
            setCardNumber(target.value);
        } else if (target.name === 'expiry') {
            target.value = formatExpirationDate(expiry, target.value);
            setExpiry(target.value);
        } else if (target.name === 'cvc') {
            target.value = formatCVC(target.value, cardNumber);
            setCvc(target.value);
        } else if (target.name === 'memo') {
        }
    };

    const handlePayPalCreateOrder = async (): Promise<string> => {
        // Don't close dialogs here — the PayPalButtons component must stay mounted
        // for the PayPal SDK to open its popup. Dialogs close in handlePayPalApprove.

        // Guard: PayPal SDK may call createOrder multiple times (e.g. when user
        // switches between PayPal login and "Debit or Credit Card" inside the popup).
        // Reuse the existing order instead of creating duplicate checkout sessions.
        if (paypalOrderRef.current) {
            return paypalOrderRef.current.orderId;
        }

        const billingData: FormData = {
            email: paypalWatch.email || '',
            firstName: paypalWatch.firstName || '',
            lastName: paypalWatch.lastName || '',
            company: '',
            address: paypalWatch.address || '',
            city: paypalWatch.city || '',
            state: paypalWatch.state || '',
            zip: paypalWatch.zip || '',
            phone: paypalWatch.phone || '',
        };
        const checkoutRequest = buildCheckoutRequest(billingData, false);
        checkoutRequest.type = 'PPAL';

        // Single call: creates checkout session with server-computed prices + creates PayPal order
        const result = await cartService.createPayPalOrder(checkoutRequest);
        if (!result.ok || !result.body) {
            // Stash the server's reason so onError can show it; the SDK fires
            // onError with its own wrapped error once this promise rejects.
            paypalErrorRef.current = result.message || 'Failed to create PayPal order';
            // Duplicate-order 409: stash the existing OrderID so onError can
            // point the buyer at the order they already placed.
            paypalDuplicateOrderRef.current =
                result.error?.status === 'duplicate_recent_order' ? result.error.existingOrderId ?? null : null;
            throw new Error(paypalErrorRef.current);
        }
        paypalOrderRef.current = { token: result.body.checkoutToken, orderId: result.body.payPalOrderId };
        setPaypalCheckoutToken(result.body.checkoutToken);
        return result.body.payPalOrderId;
    };

    const handlePayPalApprove = async (data: { orderID: string }): Promise<void> => {
        // Close fallback dialogs now that PayPal flow is complete
        setOpenBlacklistedDialog(false);
        setOpenMonerisDialog(false);
        paypalOrderRef.current = null;

        // Capture via backend, which also finalizes the order through SecureWeb
        const result = await cartService.capturePayPalOrder(data.orderID, paypalCheckoutToken);
        if (result.ok && result.body?.success && result.body.orderId) {
            navigate(`/invoices/${result.body.orderId}`);
        } else {
            setDialogTitle('Transaction Failed');
            setFailMessage(result.body?.message || result.message || 'Payment capture failed');
            setOpenDialog(true);
        }
    };

    // Shared tail of every PayPal onError handler: surface the stashed server
    // reason (or fallback) in the failure dialog, expose a "View your order"
    // link if the failure was a duplicate-order 409, then clear the refs.
    const handlePayPalError = (fallback = 'Payment could not be processed.') => {
        setDialogTitle('PayPal Error');
        setFailMessage(paypalErrorRef.current || fallback);
        setDuplicateOrderId(paypalDuplicateOrderRef.current || '');
        paypalErrorRef.current = null;
        paypalDuplicateOrderRef.current = null;
        setOpenDialog(true);
    };

    const onInvoice = () => {
        setAcceptedTerms(false);
        setInvoiceDisabled(true);
        setOpenInvoiceDialog(true);
    };

    const handleAcceptTerms = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            setAcceptedTerms(true);
            setInvoiceDisabled(false);
        } else {
            setAcceptedTerms(false);
            setInvoiceDisabled(true);
        }
    };

    const handleCloseBlacklistedDialog = () => {};
    const handleCancelBlacklistedDialog = () => {
        setOpenBlacklistedDialog(false);
    };

    const handleCloseMonerisDialog = () => {};
    const handleCancelMonerisDialog = () => {
        setOpenMonerisDialog(false);
    };

    const handleCloseInvoiceDialog = () => {};
    const handleCancelInvoiceDialog = () => {
        setOpenInvoiceDialog(false);
    };

    const handleClosePromoDialog = () => {};
    const handleCancelPromoDialog = () => {
        setOpenPromoDialog(false);
    };

    const handleUpgrade = async () => {
        setOpenPromoDialog(false);

        // check if product is already in the cart
        // const promoExists = products.find((item) => item.productId === productInfo.promoProductId);
        if (upgraded) {
            setUpgraded(false);
            // revert back to original product
            const mainProduct: CartProduct = {
                productId: productInfo.productId,
                productName: productInfo.productName,
                licenseId: productInfo.licenseId,
                licenseName: productInfo.licenseName,
                price: productInfo.price,
                qty: productInfo.qty,
                amount: productInfo.amount,
            };
            const newProducts = [...products];
            newProducts[0] = mainProduct;
            setProducts(newProducts);
            const newSubtotal = newProducts.reduce((acc, item) => acc + item.amount, 0);
            setSubtotal(newSubtotal);
            setDiscount(mainDiscount);
            const tax = calcTax(newSubtotal - mainDiscount, country, canadaProvinces[canadaProvince].short);
            calcTotal(newProducts, mainDiscount, tax);
        } else {
            setUpgraded(true);
            setDiscount(productInfo.promoDiscount); // replace main product discount (if any) with promo discount

            const promoProduct: CartProduct = {
                productId: productInfo.promoProductId,
                productName: productInfo.promoProductName,
                licenseId: productInfo.promoLicenseId,
                licenseName: productInfo.promoLicenseName,
                price: productInfo.promoPrice,
                qty: productInfo.promoQty,
                amount: productInfo.promoAmount,
            };
            const newProducts = [...products];
            // replace main product with promo product
            newProducts[0] = promoProduct;
            setProducts(newProducts);
            const newSubtotal = newProducts.reduce((acc, item) => acc + item.amount, 0);
            setSubtotal(newSubtotal);
            const tax = calcTax(
                newSubtotal - productInfo.promoDiscount,
                country,
                canadaProvinces[canadaProvince].short
            );
            calcTotal(newProducts, productInfo.promoDiscount, tax);
        }
    };

    const onSubmitInvoice = async (data: FormData) => {
        setOpenInvoiceDialog(false);
        onSubmit(data, true);
    };

    // Validate the billing form before letting the PayPal popup open.
    // Returns true if all required fields pass, false otherwise.
    // Failure case surfaces in the shared openDialog so the user sees a
    // proper error rather than a silently-rejected SDK popup.
    const validateBillingForPayPal = async (): Promise<boolean> => {
        const valid = await trigger();
        if (!valid) {
            setDialogTitle('Missing Information');
            setFailMessage('Please fill in all required billing fields before paying with PayPal.');
            setOpenDialog(true);
        }
        return valid;
    };

    const buildItemsJson = (): string => {
        if (industrialProducts.length > 0) {
            const items = industrialProducts.map((item) => ({
                pid: item.id,
                product: item.product,
                lic: -4,
                qty: item.id === 91 || item.id === 92 || item.id === 99 || item.id === 88 ? item.slots ?? 0 : item.qty,
                amt: item.amt,
                price: item.price,
            }));
            return JSON.stringify(items);
        } else {
            const items = products
                .filter((item) => item.qty > 0)
                .map((item) => ({
                    pid: item.productId,
                    lic: item.licenseId,
                    qty: item.qty,
                }));
            return JSON.stringify(items);
        }
    };

    const buildBillingJson = (data: FormData): string => {
        const state =
            country === 'CA'
                ? canadaProvinces[canadaProvince].long
                : country === 'US'
                ? usaStatesAndTerritories[usaState].long
                : data.state;
        return JSON.stringify({
            firstName: data.firstName,
            lastName: data.lastName,
            company: data.company,
            address: data.address,
            city: data.city,
            state: state,
            country: country,
            zip: data.zip,
            email: data.email,
            phone: data.phone,
        });
    };

    const buildCheckoutRequest = (data: FormData, isInvoice: boolean): CheckoutCreateRequest => {
        let type = isInvoice ? 'INV' : translateCardType(issuer);
        return {
            type: type,
            discountCode: upgraded ? 'UP' : discount > 0 ? discountCode : '',
            itemsJson: industrialRaw ? '[]' : buildItemsJson(),
            billingJson: buildBillingJson(data),
            comments: upgraded ? productInfo.productShort : '',
            industrial: industrialRaw ?? undefined,
        };
    };

    const onSubmitCreditCard = async (data: FormData) => {
        onSubmit(data, false);
    };

    const onSubmit = async (data: FormData, isInvoice: boolean) => {
        if (inProgressRef.current) return;
        inProgressRef.current = true;
        setInProgress(true);

        try {
            // Step 1: Create a server-side checkout session with validated prices
            const checkoutResult = await cartService.createCheckout(buildCheckoutRequest(data, isInvoice));
            if (!checkoutResult.ok || !checkoutResult.body) {
                setDialogTitle('Transaction Failed');
                setFailMessage(checkoutResult.message || 'Failed to create checkout session');
                // Duplicate-order 409: offer a link to the order already placed.
                setDuplicateOrderId(
                    checkoutResult.error?.status === 'duplicate_recent_order'
                        ? checkoutResult.error.existingOrderId ?? ''
                        : ''
                );
                setOpenDialog(true);
                setInProgress(false);
                return;
            }
            const checkoutSession = checkoutResult.body;

            // Step 2: Submit to ASPX with the checkout token
            let request: CartCheckoutRequest = {
                bill_first_name: data.firstName,
                bill_last_name: data.lastName,
                bill_company: data.company,
                bill_address_one: data.address,
                bill_city: data.city,
                bill_state_or_province:
                    country === 'CA'
                        ? canadaProvinces[canadaProvince].long
                        : country === 'US'
                        ? usaStatesAndTerritories[usaState].long
                        : data.state,
                bill_country: country,
                bill_postal_code: data.zip,
                bill_email: data.email,
                bill_phone: data.phone,

                credit_card_brand: '',
                credit_card_number: '',
                expire_month: '',
                expire_year: '',
                cvv: '',
            };
            request['checkout_token'] = checkoutSession.token;
            if (reseller > 0) {
                request['reseller_id'] = reseller;
            }
            if (isInvoice) {
                request['order_type'] = 'INV';
            } else {
                request['credit_card_brand'] = translateCardType(issuer);
                request['credit_card_number'] = cardNumber.replaceAll(' ', '');
                request['expire_month'] = expiry.substr(0, 2);
                request['expire_year'] = '20' + expiry.substr(3, 2);
                request['cvv'] = cvc;
            }

            if (industrialProducts.length > 0) {
                industrialProducts.forEach((item, idx) => {
                    let pid = item.id;
                    let product = item.product;
                    let lic = -4; // hardware
                    var qty = item.qty;
                    if (pid === 91 || pid === 92 || pid === 99 || pid === 88) {
                        qty = item.slots ?? 0;
                    }
                    request[`product_id${idx > 0 ? idx : ''}`] = pid;
                    request[`product${idx > 0 ? idx : ''}`] = product;
                    request[`license${idx > 0 ? idx : ''}`] = lic;
                    request[`quantity${idx > 0 ? idx : ''}`] = qty;
                });
            } else {
                products.forEach((item, idx) => {
                    if (item.qty > 0) {
                        request[`product_id${idx > 0 ? idx : ''}`] = item.productId;
                        request[`product${idx > 0 ? idx : ''}`] = item.productName;
                        request[`license${idx > 0 ? idx : ''}`] = item.licenseId;
                        request[`quantity${idx > 0 ? idx : ''}`] = item.qty;
                    }
                });
            }

            const result = await cartService.checkout(request);

            if (!result.ok || !result.body) {
                // Any transport-level failure (timeout, fetch reject, 5xx) is
                // treated as ambiguous: Moneris may already have charged the card
                // (e.g. response lost mid-flight, or DB save failed after charge —
                // see OrderProcessBackend.aspx.cs bSaveFailed path). The browser
                // can't reliably distinguish pre-send failures from post-charge
                // failures, so we never invite a retry here — that's how the
                // double-charge-via-PayPal scenario happens.
                setDialogTitle('Payment status unknown');
                setFailMessage(
                    'Your payment may have been processed. Please do NOT retry. ' +
                        'Check your email for the order confirmation. If you do not see ' +
                        'it within 10 minutes, contact support@lsoft.net before placing ' +
                        'another order.'
                );
                setOpenDialog(true);
                setInProgress(false);
            } else if (!result.body.status) {
                const response = result.body;
                if (response.message === 'Blacklisted') {
                    setOpenBlacklistedDialog(true);
                    setInProgress(false);
                } else if (response.message === 'moneris problem') {
                    setOpenMonerisDialog(true);
                    setInProgress(false);
                } else {
                    setDialogTitle('Transaction Failed');
                    setFailMessage(response.message);
                    setOpenDialog(true);
                    setInProgress(false);
                }
            } else {
                const response = result.body;
                const toast_message = isInvoice
                    ? 'Invoice was successfully created'
                    : 'Payment was successfully processed';
                setToastState({ open: true, status: 'success', message: toast_message });
                setIssuer('');
                setCardNumber('');
                setExpiry('');
                setCvc('');
                setInProgress(false);
                navigate(`/invoices/${response.order}`);
            }
        } finally {
            inProgressRef.current = false;
        }
    };

    const handleCloseSnackbar = (event?: React.SyntheticEvent, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setToastState({ ...toastState, open: false });
    };

    const onCountryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newCountry = event.target.value;
        const tax = calcTax(subtotal - discount, newCountry, canadaProvinces[canadaProvince].short);
        if (industrialProducts.length > 0) {
            calcIndustrialTotal(industrialProducts, tax);
        } else {
            calcTotal(products, discount, tax);
        }
        setCountry(newCountry);
    };

    const onProvinceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newProv = event.target.value;
        const tax = calcTax(subtotal - discount, country, canadaProvinces[newProv].short);
        if (industrialProducts.length > 0) {
            calcIndustrialTotal(industrialProducts, tax);
        } else {
            calcTotal(products, discount, tax);
        }
        setCanadaProvince(Number.parseInt(newProv));
    };

    const onStateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setUsaState(Number.parseInt(event.target.value));
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setDuplicateOrderId('');
        if (gotoHome) {
            navigate('/');
        }
    };

    const calcTax = (beforeTaxes: number, newCountry: string, newProvince: string): number => {
        let totalTax = 0;

        if (newCountry === 'CA') {
            let _gst = 0;
            let _pst = 0;
            switch (newProvince) {
                case 'AB':
                case 'NT':
                case 'NU':
                case 'YT':
                    _gst = parseFloat((beforeTaxes * 0.05).toFixed(2));
                    setTaxes([{ name: 'GST (5%)', amount: _gst }]);
                    break;
                case 'BC':
                case 'MB':
                    _gst = parseFloat((beforeTaxes * 0.05).toFixed(2));
                    _pst = parseFloat((beforeTaxes * 0.07).toFixed(2));
                    setTaxes([
                        { name: 'GST (5%)', amount: _gst },
                        { name: 'PST (7%)', amount: _pst },
                    ]);
                    break;
                case 'NB':
                case 'NL':
                case 'NS':
                case 'PE':
                    _gst = parseFloat((beforeTaxes * 0.05).toFixed(2));
                    _pst = parseFloat((beforeTaxes * 0.1).toFixed(2));
                    setTaxes([{ name: 'HST (15%)', amount: _gst + _pst }]);
                    break;
                case 'ON':
                    _gst = parseFloat((beforeTaxes * 0.05).toFixed(2));
                    _pst = parseFloat((beforeTaxes * 0.08).toFixed(2));
                    setTaxes([{ name: 'HST (13%)', amount: _gst + _pst }]);
                    break;
                case 'QC':
                    _gst = parseFloat((beforeTaxes * 0.05).toFixed(2));
                    _pst = parseFloat((beforeTaxes * 0.09975).toFixed(2));
                    setTaxes([
                        { name: 'GST (5%)', amount: _gst },
                        { name: 'QST (9.975%)', amount: _pst },
                    ]);
                    break;
                case 'SK':
                    _gst = parseFloat((beforeTaxes * 0.05).toFixed(2));
                    _pst = parseFloat((beforeTaxes * 0.06).toFixed(2));
                    setTaxes([
                        { name: 'GST (5%)', amount: _gst },
                        { name: 'PST (6%)', amount: _pst },
                    ]);
                    break;
                default:
                    _gst = parseFloat((beforeTaxes * 0.05).toFixed(2));
                    setTaxes([{ name: 'GST (5%)', amount: _gst }]);
                    break;
            }
            setGst(_gst);
            setPst(_pst);
            totalTax = _gst + _pst;
        } else {
            setGst(0);
            setPst(0);
            setTaxes([]);
            totalTax = 0;
        }
        return totalTax;
    };
    // #endregion

    return (
        <>
            <Title title="Checkout" />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    py: 3,
                }}
            >
                <Container maxWidth="lg">
                    <Dialog
                        open={openDialog}
                        onClose={handleCloseDialog}
                        aria-labelledby="alert-dialog-title"
                        aria-describedby="alert-dialog-description"
                    >
                        <DialogTitle id="alert-dialog-title">{dialogTitle}</DialogTitle>
                        <DialogContent>
                            <DialogContentText id="alert-dialog-description">{failMessage}</DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            {duplicateOrderId && (
                                <Button
                                    onClick={() => {
                                        setOpenDialog(false);
                                        setDuplicateOrderId('');
                                        navigate(`/invoices/${duplicateOrderId}`);
                                    }}
                                >
                                    View your order
                                </Button>
                            )}
                            <Button onClick={handleCloseDialog} autoFocus>
                                OK
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Dialog
                        open={openPromoDialog}
                        onClose={handleClosePromoDialog}
                        aria-labelledby="alert-dialog-title"
                        aria-describedby="alert-dialog-description"
                    >
                        <DialogContent>
                            <PromoTitle>Active@ Data Studio</PromoTitle>
                            <PromoSubtitle>12 disk tools in one package</PromoSubtitle>
                            <Box sx={{ mt: 2 }}>
                                {productInfo && productInfo.includedInDataStudio && (
                                    <Typography sx={{ my: 2, mx: 3, fontSize: 12 }}>
                                        <span style={{ fontWeight: 'bold' }}>
                                            Save ${numeral(productInfo.promoSavings).format('0,0.00')}
                                        </span>{' '}
                                        (cost of {productInfo.includedProductName} (included) plus extra $
                                        {numeral(productInfo.promoDiscount).format('0,0.00')} discount off the regular
                                        Data Studio price) by{' '}
                                        <span style={{ fontWeight: 'bold' }}>
                                            upgrading to Active@ Data Studio for $
                                            {numeral(productInfo.promoAmount - productInfo.promoDiscount).format(
                                                '0,0.00'
                                            )}
                                        </span>
                                        .
                                    </Typography>
                                )}
                                {productInfo && !productInfo.includedInDataStudio && (
                                    <Typography sx={{ my: 1, fontSize: 13 }}>
                                        <span style={{ fontWeight: 'bold' }}>
                                            Save ${numeral(productInfo.promoSavings).format('0,0.00')}
                                        </span>{' '}
                                        (discount off the regular Data Studio price) by{' '}
                                        <span style={{ fontWeight: 'bold' }}>
                                            upgrading to Active@ Data Studio for $
                                            {numeral(productInfo.promoAmount - productInfo.promoDiscount).format(
                                                '0,0.00'
                                            )}
                                        </span>
                                        .
                                    </Typography>
                                )}
                            </Box>

                            <Grid container spacing={2} sx={{ padding: '0px' }}>
                                <Grid item xs={12} sm={6}>
                                    <PromoCard sx={{ backgroundColor: '#f8f9fa' }}>
                                        <PromoProduct>
                                            <img src="/static/icons/boot-disk.png" width="40" alt="boot disk" />
                                            <PromoName>Active@ Boot Disk</PromoName>
                                            <PromoDescr>
                                                Operating system on CD/USB disk. Gives full access to even non-bootable
                                                PC and provides a platform for data recovery tools.
                                            </PromoDescr>
                                        </PromoProduct>
                                    </PromoCard>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <PromoCard sx={{ backgroundColor: '#f8f9fa' }}>
                                        <PromoProduct>
                                            <img src="/static/icons/killdisk.png" width="40" alt="killdisk" />
                                            <PromoName>Active@ KillDisk Professional</PromoName>
                                            <PromoDescr>
                                                Destroy all data on hard disks, excluding any possibility of deleted
                                                files and folders data recovery. Supports more than 20 international
                                                data sanitizing standards.
                                            </PromoDescr>
                                        </PromoProduct>
                                    </PromoCard>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <PromoCard sx={{ backgroundColor: '#f8f9fa' }}>
                                        <PromoProduct>
                                            <img src="/static/icons/disk-image.png" width="40" alt="backup" />
                                            <PromoName>Active@ Disk Image</PromoName>
                                            <PromoDescr>
                                                Disk image software that makes an exact copy of any PC disk for backup
                                                purposes.
                                            </PromoDescr>
                                        </PromoProduct>
                                    </PromoCard>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <PromoCard sx={{ backgroundColor: '#f8f9fa' }}>
                                        <PromoProduct>
                                            <img src="/static/icons/file-recovery.png" width="40" alt="file recovery" />
                                            <PromoName>Active@ File Recovery</PromoName>
                                            <PromoDescr>
                                                Restore deleted or lost files, damaged or re-formatted volumes, even if
                                                your PC doesn't boot. Includes advanced disk editor and Virtual RAID
                                                re-constructor.
                                            </PromoDescr>
                                        </PromoProduct>
                                    </PromoCard>
                                </Grid>
                            </Grid>
                            <Typography variant="body2" sx={{ fontSize: 12, fontStyle: 'italic', mt: 2, mb: 2, mx: 3 }}>
                                Includes{' '}
                                <PromoLink href="https://www.lsoft.net/bootdisk/" target="_blank">
                                    Active@ Boot Disk
                                </PromoLink>
                                ,{' '}
                                <PromoLink href="https://www.lsoft.net/killdisk/" target="_blank">
                                    Active@ KillDisk
                                </PromoLink>
                                ,{' '}
                                <PromoLink href="https://www.lsoft.net/disk-image/" target="_blank">
                                    Active@ Disk Image
                                </PromoLink>
                                ,{' '}
                                <PromoLink href="https://www.lsoft.net/file-recovery/" target="_blank">
                                    Active@ File Recovery
                                </PromoLink>
                                ,{' '}
                                <PromoLink href="https://www.lsoft.net/partition-recovery/" target="_blank">
                                    Active@ Partition Recovery
                                </PromoLink>
                                ,{' '}
                                <PromoLink href="https://www.lsoft.net/undelete/" target="_blank">
                                    Active@ UNDELETE
                                </PromoLink>
                                {', '}
                                <PromoLink href="https://www.lsoft.net/password-changer/" target="_blank">
                                    Active@ Password Changer
                                </PromoLink>{' '}
                                and more...
                            </Typography>

                            <PromoMore>
                                <a href="https://www.lsoft.net/data-studio/" target="_blank" rel="noreferrer">
                                    Learn more about Data Studio »
                                </a>
                            </PromoMore>
                        </DialogContent>
                        <DialogActions>
                            <Button sx={{ mr: 2 }} onClick={handleCancelPromoDialog}>
                                Cancel
                            </Button>
                            <Button sx={{ mr: 2 }} color="primary" variant="contained" onClick={handleUpgrade}>
                                Upgrade
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Dialog
                        open={openBlacklistedDialog}
                        onClose={handleCloseBlacklistedDialog}
                        aria-labelledby="alert-dialog-title"
                        aria-describedby="alert-dialog-description"
                    >
                        <DialogTitle id="alert-dialog-title">Payment pre-processing validation failed</DialogTitle>
                        <DialogContent>
                            <DialogContentText id="alert-dialog-description">
                                Please use PayPal to process your payment. You can pay with your PayPal account or
                                Credit/Debit Card as a guest. After successful payment you will be redirected back here
                                to get an invoice and registration key.
                            </DialogContentText>
                            <DialogContentText sx={{ mt: 2 }}>
                                You can also contact our sales department {settings.phone1} for order processing over
                                the phone.
                            </DialogContentText>
                            <Box sx={{ mt: 2 }}>
                                <PayPalScriptProvider
                                    options={{ clientId: paypalClientId, currency: 'USD', disableFunding: 'paylater' }}
                                >
                                    <PayPalButtons
                                        style={{
                                            layout: 'vertical',
                                            color: 'gold',
                                            shape: 'rect',
                                            label: 'paypal',
                                        }}
                                        createOrder={handlePayPalCreateOrder}
                                        onApprove={handlePayPalApprove}
                                        onError={() => {
                                            setOpenBlacklistedDialog(false);
                                            handlePayPalError();
                                        }}
                                    />
                                </PayPalScriptProvider>
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button sx={{ mr: 2 }} onClick={handleCancelBlacklistedDialog} autoFocus>
                                Cancel
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Dialog
                        open={openMonerisDialog}
                        onClose={handleCloseMonerisDialog}
                        aria-labelledby="alert-dialog-title"
                        aria-describedby="alert-dialog-description"
                    >
                        <DialogTitle id="alert-dialog-title">Payment processing failed</DialogTitle>
                        <DialogContent>
                            <DialogContentText id="alert-dialog-description">
                                We experience payment processing issues with our provider. You can try again later, or
                                use PayPal to process your payment.
                            </DialogContentText>
                            <DialogContentText sx={{ mt: 2 }}>
                                You can also contact our sales department {settings.phone1} for order processing over
                                the phone.
                            </DialogContentText>
                            <Box sx={{ mt: 2 }}>
                                <PayPalScriptProvider
                                    options={{ clientId: paypalClientId, currency: 'USD', disableFunding: 'paylater' }}
                                >
                                    <PayPalButtons
                                        style={{
                                            layout: 'vertical',
                                            color: 'gold',
                                            shape: 'rect',
                                            label: 'paypal',
                                        }}
                                        createOrder={handlePayPalCreateOrder}
                                        onApprove={handlePayPalApprove}
                                        onError={() => {
                                            setOpenMonerisDialog(false);
                                            handlePayPalError();
                                        }}
                                    />
                                </PayPalScriptProvider>
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button sx={{ mr: 2 }} onClick={handleCancelMonerisDialog} autoFocus>
                                Cancel
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Dialog
                        open={openInvoiceDialog}
                        onClose={handleCloseInvoiceDialog}
                        aria-labelledby="alert-dialog-title"
                        aria-describedby="alert-dialog-description"
                    >
                        <DialogTitle id="alert-dialog-title">Software delivery terms</DialogTitle>
                        <DialogContent>
                            <DialogContentText id="alert-dialog-description">
                                Software download will only be available after invoice has been paid.
                            </DialogContentText>
                            <Box sx={{ mt: 2 }}>
                                <Checkbox
                                    checked={acceptedTerms}
                                    value={acceptedTerms}
                                    color="primary"
                                    onChange={handleAcceptTerms}
                                />
                                I agree to software delivery terms
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button sx={{ mr: 2 }} onClick={handleCancelInvoiceDialog} autoFocus>
                                Cancel
                            </Button>
                            <form id="payment" onSubmit={handleSubmit(onSubmitInvoice)}>
                                <Button
                                    sx={{ mr: 2 }}
                                    color="primary"
                                    variant="contained"
                                    type="submit"
                                    disabled={invoiceDisabled}
                                >
                                    OK
                                </Button>
                            </form>
                        </DialogActions>
                    </Dialog>

                    <Snackbar
                        open={toastState.open}
                        autoHideDuration={6000}
                        onClose={handleCloseSnackbar}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    >
                        <Alert onClose={handleCloseSnackbar} severity={toastState.status}>
                            {toastState.message}
                        </Alert>
                    </Snackbar>

                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Card>
                                <CardHeader title="Order Summary" />
                                {industrialProducts.length > 0 && (
                                    <CardContent sx={{ py: 0 }}>
                                        <Scrollbar>
                                            <Box sx={{ minWidth: 700 }}>
                                                <Table>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Product</TableCell>
                                                            <TableCell align="right">Quantity</TableCell>
                                                            <TableCell align="right">Slots</TableCell>
                                                            <TableCell align="right">Amount</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {industrialProducts.map((product, idx) => (
                                                            <TableRow key={idx}>
                                                                <TableCell>{product.product}</TableCell>
                                                                <TableCell align="right">
                                                                    {product.qty > 0 ? product.qty : ''}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    {product.slots ?? ''}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    {product.qty > 0
                                                                        ? '$' + numeral(product.amt).format('0,0.00')
                                                                        : ''}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}

                                                        <TableRow>
                                                            <TableCell />
                                                            <TableCell />
                                                            <TableCell align="right">
                                                                <Typography style={{ fontWeight: 600 }}>
                                                                    Subtotal
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Typography style={{ fontWeight: 600 }}>
                                                                    ${numeral(subtotal).format('0,0.00')}
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>

                                                        {taxes.map((tax, index) => {
                                                            return (
                                                                <TableRow key={index}>
                                                                    <TableCell />
                                                                    <TableCell />
                                                                    <TableCell align="right">{tax.name}</TableCell>
                                                                    <TableCell align="right">
                                                                        ${numeral(tax.amount).format('0,0.00')}
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })}

                                                        <TableRow>
                                                            <TableCell />
                                                            <TableCell />
                                                            <TableCell align="right">
                                                                <Typography style={{ fontWeight: 600 }}>
                                                                    Order Total
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Typography style={{ fontWeight: 600 }}>
                                                                    US ${numeral(total).format('0,0.00')}
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableBody>
                                                </Table>

                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        alignContent: 'center',
                                                        justifyContent: 'space-between',
                                                        pt: 2,
                                                    }}
                                                >
                                                    {industrialProducts.length === 0 && (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                alignContent: 'center',
                                                                visibility: `${
                                                                    productInfo && productInfo.showPromo ? '' : 'hidden'
                                                                }`,
                                                            }}
                                                        >
                                                            {' '}
                                                            <a
                                                                href="https://www.disktools.com/"
                                                                target="_blank"
                                                                rel="noreferrer"
                                                            >
                                                                <img
                                                                    src="/static/images/datastudio.png"
                                                                    width="150"
                                                                    alt="data studio"
                                                                />
                                                            </a>
                                                            <Box>
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        alignContent: 'center',
                                                                    }}
                                                                >
                                                                    <PromoDialogLink onClick={onPromoInfoClick}>
                                                                        <Typography sx={{ fontSize: 20 }}>
                                                                            Consider Active@ Data Studio Toolkit{' '}
                                                                        </Typography>{' '}
                                                                    </PromoDialogLink>
                                                                    <IconButton
                                                                        color="primary"
                                                                        aria-label="more info"
                                                                        onClick={onPromoInfoClick}
                                                                    >
                                                                        <HelpOutlineIcon />
                                                                    </IconButton>
                                                                </Box>
                                                                <PromoDialogLink onClick={onPromoInfoClick}>
                                                                    <Typography
                                                                        variant="overline"
                                                                        gutterBottom
                                                                        sx={{
                                                                            fontSize: 12,
                                                                            display: 'block',
                                                                            lineHeight: '15px',
                                                                        }}
                                                                    >
                                                                        {productInfo && productInfo.includedInDataStudio
                                                                            ? `${removePrefix(
                                                                                  productInfo.areaProduct,
                                                                                  'Active@ '
                                                                              )} plus 11 Disk Tools in One Package!`
                                                                            : '12 Disk Tools in One Package!'}
                                                                    </Typography>{' '}
                                                                </PromoDialogLink>
                                                            </Box>
                                                            <Button
                                                                variant="contained"
                                                                sx={{ m: 5, px: 4 }}
                                                                onClick={handleUpgrade}
                                                                size="large"
                                                            >
                                                                {upgraded ? 'UNDO' : 'UPGRADE'}
                                                            </Button>
                                                        </Box>
                                                    )}

                                                    {industrialProducts.length === 0 && (
                                                        <>
                                                            <form onSubmit={handleSubmitForm1(onSubmitDiscountForm)}>
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        alignContent: 'center',
                                                                        visibility: `${upgraded ? 'hidden' : ''}`,
                                                                    }}
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            visibility: `${
                                                                                productInfo && productInfo.showPromo
                                                                                    ? ''
                                                                                    : 'hidden'
                                                                            }`,
                                                                        }}
                                                                    >
                                                                        <VertDivider> &nbsp;</VertDivider>
                                                                    </Box>
                                                                    <TextField
                                                                        size="small"
                                                                        label="Discount"
                                                                        sx={{ width: 150 }}
                                                                        {...registerForm1('discountCode')}
                                                                    />
                                                                    {errorsForm1.discountCode && (
                                                                        <p>{errorsForm1.discountCode.message}</p>
                                                                    )}
                                                                    <Button
                                                                        sx={{ m: 1 }}
                                                                        type="submit"
                                                                        variant="contained"
                                                                        onClick={onSubmitDiscountForm}
                                                                    >
                                                                        Apply
                                                                    </Button>
                                                                </Box>
                                                            </form>
                                                        </>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Scrollbar>
                                    </CardContent>
                                )}
                                {industrialProducts.length === 0 && (
                                    <CardContent sx={{ py: 0 }}>
                                        <Scrollbar>
                                            <Box sx={{ minWidth: 700 }}>
                                                <Table>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Product</TableCell>
                                                            <TableCell>License</TableCell>
                                                            <TableCell align="right">Price</TableCell>
                                                            <TableCell align="right">Quantity</TableCell>
                                                            <TableCell align="right">Amount</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {products.map((product, idx) => (
                                                            <TableRow key={idx}>
                                                                <TableCell>
                                                                    {product.productId === ANNUAL_SUPPORT_ID ? (
                                                                        <FormControl
                                                                            variant="standard"
                                                                            sx={{ m: 0, minWidth: 120 }}
                                                                        >
                                                                            <Select
                                                                                id="support-select-standard"
                                                                                value={product.qty}
                                                                                onChange={handleSupportChange}
                                                                                sx={{ fontSize: 14 }}
                                                                            >
                                                                                <MenuItem value={0}>
                                                                                    <em>
                                                                                        Annual Support and Updates - 1
                                                                                        year (INCLUDED)
                                                                                    </em>
                                                                                </MenuItem>
                                                                                <MenuItem value={1}>
                                                                                    Annual Support and Updates - 2 years
                                                                                </MenuItem>
                                                                                <MenuItem value={2}>
                                                                                    Annual Support and Updates - 3 years
                                                                                </MenuItem>
                                                                                <MenuItem value={4}>
                                                                                    Annual Support and Updates - 5 years
                                                                                </MenuItem>
                                                                                <MenuItem value={9}>
                                                                                    Annual Support and Updates - 10
                                                                                    years
                                                                                </MenuItem>
                                                                                <MenuItem
                                                                                    value={LIFETIME_SUPPORT_YEARS}
                                                                                >
                                                                                    Annual Support and Updates -
                                                                                    Lifetime
                                                                                </MenuItem>
                                                                            </Select>
                                                                        </FormControl>
                                                                    ) : (
                                                                        product.productName
                                                                    )}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {product.qty > 0 ? product.licenseName : ''}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    {product.qty > 0
                                                                        ? '$' + numeral(product.price).format('0,0.00')
                                                                        : ''}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    {product.qty > 0 ? product.qty : ''}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    {product.qty > 0
                                                                        ? '$' + numeral(product.amount).format('0,0.00')
                                                                        : ''}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}

                                                        <TableRow>
                                                            <TableCell />
                                                            <TableCell />
                                                            <TableCell />
                                                            <TableCell align="right">
                                                                <Typography style={{ fontWeight: 600 }}>
                                                                    Subtotal
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Typography style={{ fontWeight: 600 }}>
                                                                    ${numeral(subtotal).format('0,0.00')}
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>

                                                        {discount > 0 && (
                                                            <TableRow>
                                                                <TableCell />
                                                                <TableCell />
                                                                <TableCell />
                                                                <TableCell align="right">
                                                                    <Typography style={{ fontWeight: 600 }}>
                                                                        Discount
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <Typography style={{ fontWeight: 600 }}>
                                                                        -$
                                                                        {numeral(discount).format('0,0.00')}
                                                                    </Typography>
                                                                </TableCell>
                                                            </TableRow>
                                                        )}

                                                        {taxes.map((tax, index) => {
                                                            return (
                                                                <TableRow key={index}>
                                                                    <TableCell />
                                                                    <TableCell />
                                                                    <TableCell />
                                                                    <TableCell align="right">{tax.name}</TableCell>
                                                                    <TableCell align="right">
                                                                        ${numeral(tax.amount).format('0,0.00')}
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })}

                                                        <TableRow>
                                                            <TableCell />
                                                            <TableCell />
                                                            <TableCell />
                                                            <TableCell align="right">
                                                                <Typography style={{ fontWeight: 600 }}>
                                                                    Order Total
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Typography style={{ fontWeight: 600 }}>
                                                                    US ${numeral(total).format('0,0.00')}
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableBody>
                                                </Table>

                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        alignContent: 'center',
                                                        justifyContent: 'space-between',
                                                        pt: 2,
                                                    }}
                                                >
                                                    {true && (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                alignContent: 'center',
                                                                visibility: `${
                                                                    productInfo && productInfo.showPromo ? '' : 'hidden'
                                                                }`,
                                                            }}
                                                        >
                                                            {' '}
                                                            <a
                                                                href="https://www.disktools.com/"
                                                                target="_blank"
                                                                rel="noreferrer"
                                                            >
                                                                <img
                                                                    src="/static/images/datastudio.png"
                                                                    width="150"
                                                                    alt="data studio"
                                                                />
                                                            </a>
                                                            <Box>
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        alignContent: 'center',
                                                                    }}
                                                                >
                                                                    <PromoDialogLink onClick={onPromoInfoClick}>
                                                                        <Typography sx={{ fontSize: 20 }}>
                                                                            Consider Active@ Data Studio Toolkit{' '}
                                                                        </Typography>{' '}
                                                                    </PromoDialogLink>
                                                                    <IconButton
                                                                        color="primary"
                                                                        aria-label="more info"
                                                                        onClick={onPromoInfoClick}
                                                                    >
                                                                        <HelpOutlineIcon />
                                                                    </IconButton>
                                                                </Box>
                                                                <PromoDialogLink onClick={onPromoInfoClick}>
                                                                    <Typography
                                                                        variant="overline"
                                                                        gutterBottom
                                                                        sx={{
                                                                            fontSize: 12,
                                                                            display: 'block',
                                                                            lineHeight: '15px',
                                                                        }}
                                                                    >
                                                                        {productInfo && productInfo.includedInDataStudio
                                                                            ? `${removePrefix(
                                                                                  productInfo.areaProduct,
                                                                                  'Active@ '
                                                                              )} plus 11 Disk Tools in One Package!`
                                                                            : '12 Disk Tools in One Package!'}
                                                                    </Typography>{' '}
                                                                </PromoDialogLink>
                                                            </Box>
                                                            <Button
                                                                variant="contained"
                                                                sx={{ m: 5, px: 4 }}
                                                                onClick={handleUpgrade}
                                                                size="large"
                                                            >
                                                                {upgraded ? 'UNDO' : 'UPGRADE'}
                                                            </Button>
                                                        </Box>
                                                    )}

                                                    <>
                                                        <form onSubmit={handleSubmitForm1(onSubmitDiscountForm)}>
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    alignContent: 'center',
                                                                    visibility: `${upgraded ? 'hidden' : ''}`,
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        visibility: `${
                                                                            productInfo && productInfo.showPromo
                                                                                ? ''
                                                                                : 'hidden'
                                                                        }`,
                                                                    }}
                                                                >
                                                                    <VertDivider> &nbsp;</VertDivider>
                                                                </Box>
                                                                <TextField
                                                                    size="small"
                                                                    label="Discount"
                                                                    sx={{ width: 150 }}
                                                                    {...registerForm1('discountCode')}
                                                                />
                                                                {errorsForm1.discountCode && (
                                                                    <p>{errorsForm1.discountCode.message}</p>
                                                                )}
                                                                <Button
                                                                    sx={{ m: 1 }}
                                                                    type="submit"
                                                                    variant="contained"
                                                                    onClick={onSubmitDiscountForm}
                                                                >
                                                                    Apply
                                                                </Button>
                                                            </Box>
                                                        </form>
                                                    </>
                                                </Box>
                                            </Box>
                                        </Scrollbar>
                                    </CardContent>
                                )}
                            </Card>
                        </Grid>
                        <Grid item md={6} xs={12}>
                            <Box sx={{ height: '100%' }}>
                                <Card sx={{ p: 0, height: '100%' }}>
                                    <Box sx={{ p: 3 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <Typography variant="h6">Billing Information</Typography>
                                        </Box>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Controller
                                                name="firstName"
                                                control={control}
                                                defaultValue={billing.firstName}
                                                rules={{
                                                    required: 'First Name is required',
                                                    maxLength: {
                                                        value: 150,
                                                        message: 'First Name cannot exceed 150 characters',
                                                    },
                                                    pattern: {
                                                        value: /^[A-Za-zÀ-ž0-9\s.,:/\-&()#]+$/,
                                                        message: 'Invalid character entered',
                                                    },
                                                }}
                                                render={({ field, fieldState: { error } }) => (
                                                    <TextField
                                                        {...field}
                                                        required
                                                        label="First Name"
                                                        size="small"
                                                        sx={{
                                                            flexGrow: 1,
                                                        }}
                                                        error={!!error}
                                                        helperText={error ? error.message : null}
                                                    />
                                                )}
                                            />
                                        </Box>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                mt: 2,
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Controller
                                                name="lastName"
                                                control={control}
                                                defaultValue={billing.lastName}
                                                rules={{
                                                    required: 'Last Name is required',
                                                    maxLength: {
                                                        value: 150,
                                                        message: 'Last Name cannot exceed 150 characters',
                                                    },
                                                    pattern: {
                                                        value: /^[A-Za-zÀ-ž0-9\s.,:/\-&()#]+$/,
                                                        message: 'Invalid character entered',
                                                    },
                                                }}
                                                render={({ field, fieldState: { error } }) => (
                                                    <TextField
                                                        {...field}
                                                        required
                                                        label="Last Name"
                                                        size="small"
                                                        sx={{
                                                            flexGrow: 1,
                                                        }}
                                                        error={!!error}
                                                        helperText={error ? error.message : null}
                                                    />
                                                )}
                                            />
                                        </Box>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                mt: 2,
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Controller
                                                name="company"
                                                control={control}
                                                defaultValue={billing.company}
                                                rules={{
                                                    maxLength: {
                                                        value: 150,
                                                        message: 'Company name cannot exceed 150 characters',
                                                    },
                                                    pattern: {
                                                        value: /^[A-Za-zÀ-ž0-9\s.,:/\-&()#]+$/,
                                                        message: 'Invalid character entered',
                                                    },
                                                }}
                                                render={({ field, fieldState: { error } }) => (
                                                    <TextField
                                                        {...field}
                                                        label="Company"
                                                        size="small"
                                                        sx={{
                                                            flexGrow: 1,
                                                        }}
                                                        error={!!error}
                                                        helperText={error ? error.message : null}
                                                    />
                                                )}
                                            />
                                        </Box>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                mt: 2,
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Controller
                                                name="address"
                                                control={control}
                                                defaultValue={billing.address}
                                                rules={{
                                                    maxLength: {
                                                        value: 150,
                                                        message: 'Address cannot exceed 150 characters',
                                                    },
                                                    pattern: {
                                                        value: /^[A-Za-zÀ-ž0-9\s.,:/\-&()#]+$/,
                                                        message: 'Invalid character entered',
                                                    },
                                                }}
                                                render={({ field, fieldState: { error } }) => (
                                                    <TextField
                                                        {...field}
                                                        label="Address"
                                                        size="small"
                                                        sx={{
                                                            flexGrow: 1,
                                                        }}
                                                        error={!!error}
                                                        helperText={error ? error.message : null}
                                                    />
                                                )}
                                            />
                                        </Box>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                mt: 2,
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Controller
                                                name="city"
                                                control={control}
                                                defaultValue={billing.city}
                                                rules={{
                                                    maxLength: {
                                                        value: 150,
                                                        message: 'City name cannot exceed 150 characters',
                                                    },
                                                    pattern: {
                                                        value: /^[A-Za-zÀ-ž0-9\s.,:/\-&()#]+$/,
                                                        message: 'Invalid character entered',
                                                    },
                                                }}
                                                render={({ field, fieldState: { error } }) => (
                                                    <TextField
                                                        {...field}
                                                        label="City"
                                                        size="small"
                                                        sx={{
                                                            flexGrow: 1,
                                                        }}
                                                        error={!!error}
                                                        helperText={error ? error.message : null}
                                                    />
                                                )}
                                            />
                                        </Box>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                mt: 2,
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Controller
                                                name="zip"
                                                control={control}
                                                defaultValue={billing.zip}
                                                rules={{
                                                    maxLength: {
                                                        value: 150,
                                                        message: 'Zip/postal code cannot exceed 150 characters',
                                                    },
                                                    pattern: {
                                                        value: /^[A-Za-zÀ-ž0-9\s.,:/\-&()#]+$/,
                                                        message: 'Invalid character entered',
                                                    },
                                                }}
                                                render={({ field, fieldState: { error } }) => (
                                                    <TextField
                                                        {...field}
                                                        label="Zip / Postal Code"
                                                        size="small"
                                                        sx={{
                                                            flexGrow: 1,
                                                        }}
                                                        error={!!error}
                                                        helperText={error ? error.message : null}
                                                    />
                                                )}
                                            />
                                        </Box>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                mt: 2,
                                                alignItems: 'center',
                                            }}
                                        >
                                            <TextField
                                                label="Country"
                                                size="small"
                                                select
                                                sx={{
                                                    flexGrow: 1,
                                                }}
                                                value={country}
                                                onChange={onCountryChange}
                                            >
                                                {countries.map((country, idx) => (
                                                    <MenuItem key={idx} value={country.code}>
                                                        {country.name}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Box>

                                        {country === 'US' && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    mt: 2,
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <TextField
                                                    label="State"
                                                    size="small"
                                                    select
                                                    sx={{
                                                        flexGrow: 1,
                                                    }}
                                                    value={usaState}
                                                    onChange={onStateChange}
                                                >
                                                    {usaStatesAndTerritories.map((state, idx) => (
                                                        <MenuItem key={idx} value={idx}>
                                                            {state.long}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            </Box>
                                        )}

                                        {country === 'CA' && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    mt: 2,
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <TextField
                                                    label="Province"
                                                    size="small"
                                                    select
                                                    sx={{
                                                        flexGrow: 1,
                                                    }}
                                                    value={canadaProvince}
                                                    onChange={onProvinceChange}
                                                >
                                                    {canadaProvinces.map((prov, idx) => (
                                                        <MenuItem key={idx} value={idx}>
                                                            {prov.long}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            </Box>
                                        )}

                                        {country !== 'CA' && country !== 'US' && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    mt: 2,
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Controller
                                                    name="state"
                                                    control={control}
                                                    defaultValue={billing.state}
                                                    rules={{
                                                        maxLength: {
                                                            value: 150,
                                                            message:
                                                                'State / Province / Region cannot exceed 150 characters',
                                                        },
                                                        pattern: {
                                                            value: /^[A-Za-zÀ-ž0-9\s.,:/\-&()#]+$/,
                                                            message: 'Invalid character entered',
                                                        },
                                                    }}
                                                    render={({ field, fieldState: { error } }) => (
                                                        <TextField
                                                            {...field}
                                                            label="State / Province / Region"
                                                            size="small"
                                                            sx={{
                                                                flexGrow: 1,
                                                            }}
                                                            error={!!error}
                                                            helperText={error ? error.message : null}
                                                        />
                                                    )}
                                                />
                                            </Box>
                                        )}

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                mt: 2,
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Controller
                                                name="phone"
                                                control={control}
                                                defaultValue={billing.phone}
                                                rules={{
                                                    required: 'Phone is required',
                                                    maxLength: {
                                                        value: 150,
                                                        message: 'Phone cannot exceed 150 characters',
                                                    },
                                                    pattern: {
                                                        value: /^[A-Za-zÀ-ž0-9\s.,:/\-&()#]+$/,
                                                        message: 'Invalid character entered',
                                                    },
                                                }}
                                                render={({ field, fieldState: { error } }) => (
                                                    <TextField
                                                        {...field}
                                                        label="Phone"
                                                        size="small"
                                                        required
                                                        sx={{
                                                            flexGrow: 1,
                                                        }}
                                                        error={!!error}
                                                        helperText={error ? error.message : null}
                                                    />
                                                )}
                                            />
                                        </Box>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                mt: 2,
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Controller
                                                name="email"
                                                control={control}
                                                defaultValue={billing.email}
                                                rules={{
                                                    required: 'Email is required',
                                                    maxLength: {
                                                        value: 150,
                                                        message: 'Email cannot exceed 150 characters',
                                                    },
                                                    pattern: {
                                                        value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                                                        message: 'Invalid email address',
                                                    },
                                                }}
                                                render={({ field, fieldState: { error } }) => (
                                                    <TextField
                                                        {...field}
                                                        label="Email"
                                                        size="small"
                                                        required
                                                        sx={{
                                                            flexGrow: 1,
                                                        }}
                                                        error={!!error}
                                                        helperText={error ? error.message : null}
                                                    />
                                                )}
                                            />
                                        </Box>
                                    </Box>
                                </Card>
                            </Box>
                        </Grid>
                        <Grid item md={6} xs={12}>
                            <Box sx={{ height: '100%' }}>
                                <Card sx={{ height: '100%' }}>
                                    <CardContent sx={{ px: 3 }}>
                                        <Box
                                            sx={{
                                                mt: 0,
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                    <Typography variant="h6">Pay with &nbsp;</Typography>
                                                    <img alt="credit cards" style={{ width: 340 }} src={creditCards} />
                                                </Box>

                                                <CreditCard
                                                    acceptedCards={[
                                                        'visa',
                                                        'mastercard',
                                                        'amex',
                                                        'dinersclub',
                                                        'discover',
                                                    ]}
                                                    number={cardNumber}
                                                    name=""
                                                    expiry={expiry}
                                                    cvc={cvc}
                                                    focused={focused}
                                                    callback={handleCallback}
                                                />
                                            </Box>
                                            <Box>
                                                <Grid container columnSpacing={3}>
                                                    <Grid item xs={12}>
                                                        <TextField
                                                            name="number"
                                                            value={cardNumber}
                                                            required
                                                            label="Card Number"
                                                            fullWidth
                                                            onChange={handleInputChange}
                                                            onFocus={handleInputFocus}
                                                            sx={{
                                                                flexGrow: 1,
                                                                mr: 3,
                                                                mt: 3,
                                                            }}
                                                        />
                                                    </Grid>
                                                    <Grid item md={6} xs={12}>
                                                        <TextField
                                                            name="expiry"
                                                            value={expiry}
                                                            required
                                                            label="Valid Thru (MM/YY)"
                                                            fullWidth
                                                            onChange={handleInputChange}
                                                            onFocus={handleInputFocus}
                                                            sx={{
                                                                flexGrow: 1,
                                                                mr: 3,
                                                                mt: 3,
                                                            }}
                                                        />
                                                    </Grid>
                                                    <Grid item md={6} xs={12}>
                                                        <TextField
                                                            name="cvc"
                                                            value={cvc}
                                                            label="CVC"
                                                            fullWidth
                                                            onChange={handleInputChange}
                                                            onFocus={handleInputFocus}
                                                            sx={{
                                                                flexGrow: 1,
                                                                mr: 3,
                                                                mt: 3,
                                                            }}
                                                        />
                                                    </Grid>

                                                    <Grid item xs={12}>
                                                        <form id="payment" onSubmit={handleSubmit(onSubmitCreditCard)}>
                                                            <Button
                                                                color="primary"
                                                                variant="contained"
                                                                type="submit"
                                                                fullWidth
                                                                disabled={!issuer || expiry.length !== 5 || inProgress}
                                                                sx={{
                                                                    mt: 3,
                                                                    height: 48,
                                                                    fontSize: '1rem',
                                                                    fontWeight: 700,
                                                                    textTransform: 'none',
                                                                }}
                                                            >
                                                                PAY with Card
                                                            </Button>
                                                        </form>

                                                        <Divider sx={{ m: 2 }} variant="middle">
                                                            OR
                                                        </Divider>

                                                        <Box>
                                                            <PayPalScriptProvider
                                                                options={{
                                                                    clientId: paypalClientId,
                                                                    currency: 'USD',
                                                                    disableFunding: 'paylater',
                                                                }}
                                                            >
                                                                <PayPalButtons
                                                                    style={{
                                                                        layout: 'vertical',
                                                                        color: 'gold',
                                                                        shape: 'rect',
                                                                        label: 'paypal',
                                                                        height: 48,
                                                                    }}
                                                                    disabled={inProgress}
                                                                    onClick={async (_data, actions) => {
                                                                        // Validate billing before letting PayPal
                                                                        // open its popup. Rejecting here keeps the
                                                                        // SDK from launching the modal, and we
                                                                        // surface the error in openDialog.
                                                                        const valid = await validateBillingForPayPal();
                                                                        if (!valid) {
                                                                            return actions.reject();
                                                                        }
                                                                        return actions.resolve();
                                                                    }}
                                                                    createOrder={handlePayPalCreateOrder}
                                                                    onApprove={handlePayPalApprove}
                                                                    onError={() => {
                                                                        paypalOrderRef.current = null;
                                                                        handlePayPalError(
                                                                            'Payment could not be processed. Please try again.'
                                                                        );
                                                                    }}
                                                                />
                                                            </PayPalScriptProvider>
                                                        </Box>

                                                        <Divider sx={{ m: 2 }} variant="middle">
                                                            OR
                                                        </Divider>
                                                        <form id="invoice" onSubmit={handleSubmit(onInvoice)}>
                                                            <Button
                                                                color="primary"
                                                                variant="contained"
                                                                type="submit"
                                                                fullWidth
                                                                disabled={inProgress}
                                                                sx={{
                                                                    height: 48,
                                                                    fontSize: '1rem',
                                                                    fontWeight: 700,
                                                                    textTransform: 'none',
                                                                }}
                                                            >
                                                                INVOICE (to pay later)
                                                            </Button>
                                                        </form>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sx={{}}>
                            <Divider />
                            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                                <Typography variant="subtitle2">
                                    Secure payment services provided by{' '}
                                    <Box component="span" fontWeight="fontWeightBold">
                                        Royal Bank of Canada
                                    </Box>{' '}
                                    and{' '}
                                    <Box component="span" fontWeight="fontWeightBold">
                                        LSoft Technologies
                                    </Box>
                                </Typography>
                            </Box>
                            {productInfo && (
                                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                                    <Typography variant="subtitle2" fontSize="80%" sx={{ ml: 1 }}>
                                        You will be charged CAD {numeral(total * productInfo.exRate).format('0,0.00')}{' '}
                                        at rate {productInfo.exRate}
                                    </Typography>
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                </Container>
            </Box>
        </>
    );
};
